/**
 * Help inventory (card FiLhF6LRwAAP — Jonathan email 2026-10-11).
 *
 * One feature, one entry, three beats: WHAT it is, WHY it helps you as a
 * couple, and one concrete EXAMPLE. This file is the single source: the Help
 * screen renders it verbatim, and the richer tooltip labels (Thread header,
 * Us question, Requests actions, the compose tiles) are written from the same
 * beats so an explanation never disagrees with itself.
 *
 * Voice: locked design voice — plain sentence case, concierge tone, no
 * marketing froth. "Coming soon" is an honest mark, never a fake button.
 */

export interface HelpFeature {
  /** Stable id — the walk asserts each one renders in Help. */
  id: string;
  name: string;
  /** What it is. */
  what: string;
  /** Why it helps the two of you. */
  benefit: string;
  /** One concrete use. */
  example: string;
  /** Listed but not shippable yet — marked, never offered. */
  soon?: boolean;
}

export interface HelpGroup {
  tab: 'thread' | 'us' | 'life' | 'notebook' | 'settings';
  label: string;
  features: HelpFeature[];
}

export const HELP_GROUPS: HelpGroup[] = [
  {
    tab: 'thread',
    label: 'Thread',
    features: [
      {
        id: 'thread-chat',
        name: 'Chat',
        what: 'Your shared conversation — everything you both say, in order.',
        benefit: 'One thread instead of two phones, so nothing important lives only with one of you.',
        example: 'Say "running 10 late" from the train — it is on their phone before they reach the door.',
      },
      {
        id: 'thread-filters',
        name: 'Message filters',
        what: 'Chips at the top of the thread that narrow it to all messages or chat only.',
        benefit: 'Find what was said without scrolling past every request and card.',
        example: 'Tap "Chat" to reread last night’s conversation without the chores in the way.',
      },
      {
        id: 'compose-message',
        name: 'Message (the + menu)',
        what: 'A plain message, sent straight to the thread.',
        benefit: 'The fastest way to say something — no form, no fields.',
        example: 'Type "milk?" while you are thinking it.',
      },
      {
        id: 'compose-ask',
        name: 'Ask',
        what: 'A request for your partner — it lands on their list, not lost in chat.',
        benefit: 'A ask in chat scrolls away; a request stays visible until it is done.',
        example: 'Ask "Run together this week?" — they tap Yes and it marks itself done.',
      },
      {
        id: 'compose-question',
        name: 'Question',
        what: 'Today’s daily question, answered separately and revealed together.',
        benefit: 'A small reason to check in every day, and a surprise you both get to open.',
        example: 'You both answer "best meal this week" — neither sees the other until you have both written.',
      },
      {
        id: 'compose-note',
        name: 'Note',
        what: 'A note saved straight to your shared notebook.',
        benefit: 'A thought worth keeping is one tap from being findable later.',
        example: 'Save "that café on Brunswick" before you forget the name.',
      },
      {
        id: 'compose-idea',
        name: 'Idea',
        what: 'An idea parked in the notebook’s Ideas collection.',
        benefit: 'Somewhere for "we should do that" that is not a message you will both lose.',
        example: 'Park "weekend in the Yarra Valley" for the next time you plan a trip.',
      },
      {
        id: 'compose-meeting',
        name: 'Weekly sync',
        what: 'The fifteen-minute check-in that lives under Life → Plan.',
        benefit: 'The decisions from your weekly chat stop living in one person’s memory.',
        example: 'Open More → Weekly sync — "split the school run Mon/Wed" comes back as work you both keep.',
      },
      {
        id: 'compose-memory',
        name: 'Memory',
        what: 'A moment saved to your shared notebook’s Memories.',
        benefit: 'The good days stop being something you have to remember on demand.',
        example: 'Save "the whole drive home laughing" the night it happens.',
      },
      {
        id: 'compose-date',
        name: 'Date idea',
        what: 'An idea filed to the pool you both plan dates from.',
        benefit: 'Capturing it takes one line; deciding on it happens later, together.',
        example: 'Park "that gin bar on Gertrude" for the next time you plan a night out.',
      },
      {
        id: 'compose-link',
        name: 'Link',
        what: 'A page saved to the notebook — trailer, article, album.',
        benefit: 'The thing you both keep coming back to stops getting lost in chat.',
        example: 'Drop the trailer for a series you both said you would watch.',
      },
      {
        id: 'compose-kudos',
        name: 'Kudos',
        what: 'A postcard-sized thank you for something your partner did.',
        benefit: 'The work that usually goes unnoticed gets noticed, out loud.',
        example: 'Send one after they do the 6am school run — "that was lovely. Thank you."',
      },
    ],
  },
  {
    tab: 'us',
    label: 'Us',
    features: [
      {
        id: 'us-questions',
        name: 'Daily question',
        what: 'One question a day, answered separately and revealed together.',
        benefit: 'You learn something small about each other every day, without it feeling like a talk.',
        example: 'Answer "what made you laugh this week" — open both answers at once when you have both written.',
      },
      {
        id: 'us-share',
        name: 'Something to share',
        what: 'A sentence about the two of you, kept private (only me) or shared with your partner, with an option to put it on the weekly meeting agenda once it is shared.',
        benefit: 'The small thing worth saying out loud gets said at the right moment instead of being swallowed or dropped on them mid-argument.',
        example: 'Write "I loved that you took the whole morning with the kids" — keep it just yours, or widen it and arm it for Sunday’s meeting.',
      },
      {
        id: 'us-game',
        name: 'Game round',
        what: 'A quick this-or-that round for the two of you.',
        benefit: 'Play something together in the minute before dinner instead of two separate phones.',
        example: 'Pick beach over mountains — the round keeps your shared preferences.',
      },
      {
        id: 'us-dates',
        name: 'Dates',
        what: 'Ideas for time together, shaped by what you both like.',
        benefit: 'Ending the "what should we do" loop with suggestions you can veto into shape.',
        example: 'Get three ideas for Saturday night; veto the fancy one, keep the taco van.',
      },
      {
        id: 'us-digest',
        name: 'Partner digest',
        what: 'A weekly note built from what you are both interested in.',
        benefit: 'Something to talk about that you did not have to find yourself.',
        example: 'It lands with a story about the band you both follow — read it over coffee.',
      },
      {
        id: 'us-reveal-history',
        name: 'Question reveal history',
        what: 'Every question you have both answered, newest first.',
        benefit: 'See how your answers have changed since the early days.',
        example: 'Look back at your first week’s answers on your anniversary.',
      },
    ],
  },
  {
    tab: 'life',
    label: 'Life',
    features: [
      {
        id: 'life-requests',
        name: 'Requests',
        what: 'Things one of you needs from the other — ask, accept, counter or decline.',
        benefit: 'The mental load list becomes visible and shared instead of one person holding it.',
        example: 'Ask "could you do the pharmacy run?" — they counter with tomorrow, you accept.',
      },
      {
        id: 'life-chores',
        name: 'Chores',
        what: 'The recurring jobs of a shared life, tracked so nothing is only remembered by one of you.',
        benefit: 'Fairness you can see, not a feeling you argue about.',
        example: 'The bins sit on the list until someone ticks it — no reminder text needed.',
      },
      {
        id: 'life-admin',
        name: 'Inbox',
        what: 'Mail you send to your shared us@ address, sorted into what it means for the two of you — with the private me@ address for mail that stays with one of you. Every row says why it went where it went; if one landed wrong, Send it to moves it and remembers the sender, and private mail never moves.',
        benefit: 'Bills and bookings stop landing with only one person and disappearing into that inbox.',
        example: 'Forward the power bill — it lands as a card with the due date neither of you can miss, and you can re-route it if it landed wrong.',
      },
      {
        id: 'life-plan',
        name: 'Weekly sync',
        what: 'Record the week’s chat and get back appreciation, plans, fun ahead and challenges. Four things to cover: what you appreciated, what needs a decision or a share, what is coming up, and how you are both doing. Take what is useful — it is your meeting, not a form, and fifteen minutes is plenty.',
        benefit: 'A ten-minute check-in that covers what you would otherwise raise at the worst moment.',
        example: 'Record on Sunday — the draft splits into four sections you both confirm.',
      },
    ],
  },
  {
    tab: 'notebook',
    label: 'Notebook',
    features: [
      {
        id: 'nb-links',
        name: 'Collection',
        what: 'Every link you both save, with a title, art and provider worked out for you.',
        benefit: 'The "send me that again" search ends — it is all in one place, both of you can add to it.',
        example: 'Paste a recipe link; it opens with the title and site already filled in.',
      },
      {
        id: 'nb-gifts',
        name: 'Gifts',
        what: 'Gift ideas your agent collects, narrowed down to one you can actually send.',
        benefit: 'Birthdays stop being a panic — the ideas are already sitting there.',
        example: 'Three months out from their birthday, flick through ideas and keep the one that fits.',
      },
      {
        id: 'nb-memories',
        name: 'Memories',
        what: 'The moments worth keeping, saved as cards.',
        benefit: 'A shared record of the good bits you would otherwise only half-remember.',
        example: 'Save the photo from the coast trip with one line about why it mattered.',
      },
      {
        id: 'nb-dates',
        name: 'Date ideas',
        what: 'Things you both want to do, saved for when you plan.',
        benefit: 'No more scrolling at 5pm on a Friday trying to remember the place one of you liked.',
        example: 'Save the rooftop cinema link in June, use it in December.',
      },
      {
        id: 'nb-notes',
        name: 'Notes',
        what: 'Anything you both want to keep — a thought, a login hint, a name to remember.',
        benefit: 'One shared place for the small things that otherwise live in one person’s head.',
        example: 'Save the mechanic’s name both of you keep asking for.',
      },
      {
        id: 'nb-ideas',
        name: 'Ideas',
        what: 'Someday plans, parked where you will both see them again.',
        benefit: 'A "we should do that" that does not evaporate by tomorrow.',
        example: 'Park "learn to cook Thai" — it is still there when you plan a winter of weekends.',
      },
      {
        id: 'nb-scan',
        name: 'Agent scan',
        what: 'The agent reads what you save and suggests categories and tags.',
        benefit: 'Your notebook sorts itself, so finding things later costs nothing.',
        example: 'Save a gig link — it files itself under Watch with the mood tagged.',
      },
      {
        id: 'nb-search',
        name: 'Search',
        what: 'Search across everything in the notebook by word or tag.',
        benefit: 'One box finds it whether you saved it or your partner did.',
        example: 'Type "tacos" and both saved taco links come back.',
      },
    ],
  },
  {
    tab: 'settings',
    label: 'Settings',
    features: [
      {
        id: 'set-pair',
        name: 'Pairing',
        what: 'A code or invite link that brings the two of you into one space.',
        benefit: 'Exactly two people, so everything here stays yours.',
        example: 'Send the invite by link or email — your partner joins and the space opens.',
      },
      {
        id: 'set-interests',
        name: 'Your interests',
        what: 'A short questionnaire that teaches the agent what you each care about.',
        benefit: 'Suggestions arrive shaped by both of you, not a generic average.',
        example: 'Mark "live music" — the digest starts bringing gig news to the weekly note.',
      },
      {
        id: 'set-agent',
        name: 'Concierge controls',
        what: 'The switchboard for your agent: what it may do, and how much it may spend.',
        benefit: 'The help stays on the side you want it — no surprise, no spam.',
        example: 'Leave gifting on, turn chat suggestions off — the agent stays in its lane.',
      },
      {
        id: 'set-dates',
        name: 'Dates and occasions',
        what: 'Birthdays, anniversaries and your own occasions, with lead times.',
        benefit: 'The agent nudges early enough to actually do something about it.',
        example: 'Set their birthday with a 30-day lead — gift ideas start arriving in September.',
      },
      {
        id: 'set-leave',
        name: 'Sign out and leave',
        what: 'Sign out ends the session on this device; leave ends your place in the couple.',
        benefit: 'The two are separate on purpose — leaving is the only one that changes your couple.',
        example: 'New phone? Sign out here, sign in there. Leaving is only for when it is really over.',
      },
    ],
  },
];

/** Coming soon — listed in Help, clearly marked, never offered as working.
 *
 *  2026-10-16 email (card ED6amhdI1lFH): "coming soon should be commented
 *  out" — Help keeps only what works today. The entries are commented, not
 *  deleted, so they return the moment the features ship; the Help screen
 *  hides the section entirely while this list is empty. */
export const HELP_COMING_SOON: HelpFeature[] = [
  // {
  //   id: 'soon-memory-photos',
  //   name: 'Photo memories',
  //   what: 'Attach photos to a memory card.',
  //   benefit: 'The moment and the picture live in the same place.',
  //   example: 'Add the beach photo to the memory you already saved.',
  //   soon: true,
  // },
  // {
  //   id: 'soon-long-distance',
  //   name: 'Long-distance mode',
  //   what: 'Shared screens that work across time zones.',
  //   benefit: 'The gap stops meaning you plan at the wrong end of someone\u2019s day.',
  //   example: 'Leave a note at 11pm your time; it is the first thing they read at breakfast.',
  //   soon: true,
  // },
];

/** Every feature id, for the walk to assert against. */
export const HELP_FEATURE_IDS: string[] = [
  ...HELP_GROUPS.flatMap(g => g.features.map(f => f.id)),
  ...HELP_COMING_SOON.map(f => f.id),
];

/** Look up one feature's beats — tooltip copy is written from the same words. */
export function helpFeature(id: string): HelpFeature | undefined {
  for (const g of HELP_GROUPS) {
    const hit = g.features.find(f => f.id === id);
    if (hit) return hit;
  }
  return HELP_COMING_SOON.find(f => f.id === id);
}

/**
 * The tooltip label for a feature: the same three beats, in one bubble
 * (Jonathan 2026-10-11 — a one-liner is not an explanation). Benefit is the
 * sentence that sells it; the example is the sentence that lands it.
 */
export function helpTip(id: string): string {
  const f = helpFeature(id);
  if (!f) return '';
  return `${f.what} ${f.benefit} For example: ${f.example}`;
}
